import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Modal,
  Button,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Divider,
  Switch,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  createLiveStream,
  updateLiveStream,
  getLiveStreams,
} from "store/slices/liveStreamsSlice";
import { closeModal } from "store/slices/modalsSlice";
import {
  getInstructors,
  selectAssignableInstructors,
} from "store/slices/instructorsSlice";
import { getGroups } from "store/slices/groupsSlice";
import { getTrainingById, getTrainings } from "store/slices/trainingsSlice";
import { getStudents } from "store/slices/studentsSlice";
import dayjs from "dayjs";
import { limit } from "firebase/firestore/lite";

// Helper function to generate random 10-character room ID
const generateRandomRoomId = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let roomId = "";
  for (let i = 0; i < 10; i++) {
    roomId += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return roomId;
};

// Formateurs inscrits (demande approuvée) dans une formation
const getApprovedInstructorIds = (training) =>
  new Set(
    (training?.instructors || [])
      .filter((entry) => entry?.instructorId && entry.isApproved === "approved")
      .map((entry) => entry.instructorId?._id || entry.instructorId),
  );

export default function CreateEditLiveStreamModal() {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showRecordingInfoModal, setShowRecordingInfoModal] = useState(false);

  const { modalType, modalProps } = useSelector((state) => state.modals);
  const { currentLiveStream, loading } = useSelector(
    (state) => state.liveStreams,
  );

  // `training` : détail de la formation sélectionnée (contient le statut
  // d'approbation de chaque formateur, absent de la liste `trainings`)
  const { trainings, training } = useSelector((state) => state.trainings);
  const instructors = useSelector(selectAssignableInstructors);

  // OPTIONAL: only if you have these slices
  const { groups } = useSelector((state) => state.groups || { groups: [] });
  const { students } = useSelector(
    (state) => state.students || { students: [] },
  );

  const isVisible =
    modalType === "create_live_stream" || modalType === "edit_live_stream";
  const isEditMode = modalType === "edit_live_stream";

  // Helper: if you want "restrict to specific students"
  const restrictToSpecificStudents = Form.useWatch(
    "restrictToSpecificStudents",
    form,
  );

  useEffect(() => {
    if (isEditMode && modalProps?.liveStreamId) {
      form.setFieldsValue({
        training: modalProps?.data?.training?._id,
        instructor: modalProps?.data?.instructor?._id,
        group: modalProps?.data?.group?._id || null,
        title: modalProps?.data?.title,
        description: modalProps?.data?.description,
        status: modalProps?.data?.status,
        isRecording: modalProps?.data?.isRecording || false,

        scheduledStartTime: modalProps?.data?.scheduledStartTime
          ? dayjs(modalProps?.data?.scheduledStartTime)
          : null,

        // if allowedStudentIds exists and has values => restrict true
        restrictToSpecificStudents:
          (modalProps?.data?.allowedStudentIds?.length || 0) > 0,
        // FIX: Added extra optional chaining inside map to prevent 'reading _id of undefined'
        allowedStudentIds:
          modalProps?.data?.allowedStudentIds?.map((s) => s?._id || s) || [],
      });
    } else {
      form.setFieldsValue({
        status: "scheduled",
        restrictToSpecificStudents: false,
      });
    }
  }, [isEditMode, modalProps, currentLiveStream, form]);

  // Fetch instructors when modal opens
  useEffect(() => {
    if (isVisible) {
      dispatch(getTrainings({ all: true }));
      dispatch(getInstructors());
      dispatch(getGroups({ limit: 40 }));
      dispatch(getStudents());
    }
  }, [isVisible, dispatch]);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        title: values.title,
        description: values.description,
        training: values.training,
        instructor: values.instructor,
        group: values.group || null,
        roomId: isEditMode ? currentLiveStream?.roomId : generateRandomRoomId(),
        status: values.status,

        scheduledStartTime: values.scheduledStartTime?.toISOString(),

        // If not restricted => send empty array (meaning group/all allowed)
        allowedStudentIds: values.restrictToSpecificStudents
          ? values.allowedStudentIds || []
          : [],
        isRecording: values.isRecording || false,
      };

      if (isEditMode) {
        await dispatch(
          updateLiveStream({ id: modalProps.liveStreamId, data: payload }),
        ).unwrap();

        message.success(
          t("liveStreams.edit.success") || "Live stream updated successfully",
        );
      } else {
        await dispatch(createLiveStream(payload)).unwrap();
        message.success(
          t("liveStreams.createModal.success") ||
            "Live stream created successfully",
        );
      }

      dispatch(closeModal());
      dispatch(getLiveStreams({ page: 1, limit: 10 }));
      form.resetFields();
    } catch (error) {
      message.error(error?.message || error || "Failed to save live stream");
    }
  };

  const handleRecordingChange = (checked) => {
    if (checked) {
      setShowRecordingInfoModal(true);
    }
  };
  const selectedTraining = Form.useWatch("training", form);

  useEffect(() => {
    if (isVisible && selectedTraining) {
      dispatch(getTrainingById(selectedTraining));
    }
  }, [isVisible, selectedTraining, dispatch]);

  // Le select formateur ne propose que les formateurs inscrits dans la formation choisie
  const trainingInstructors = useMemo(() => {
    const approvedIds = getApprovedInstructorIds(
      training?._id === selectedTraining ? training : null,
    );
    const list = (instructors || []).filter((ins) => approvedIds.has(ins?._id));

    // En édition, garder le formateur actuel du live même s'il n'est plus inscrit
    const currentInstructor = isEditMode ? modalProps?.data?.instructor : null;
    if (
      currentInstructor?._id &&
      !list.some((ins) => ins?._id === currentInstructor._id)
    ) {
      list.push(currentInstructor);
    }
    return list;
  }, [training, instructors, selectedTraining, isEditMode, modalProps]);

  // Un formateur choisi pour une autre formation n'est plus valable
  const handleTrainingChange = () => {
    form.setFieldsValue({ instructor: undefined });
  };
  console.log(selectedTraining);
  const filteredGroups = (groups || []).filter(
    (g) => g?.formationId?._id == selectedTraining,
  );
  console.log(groups)
  useEffect(() => {
  form.setFieldsValue({ group: undefined });
}, [selectedTraining]);
  return (
    <>
      {/* Recording Info Modal */}
      <Modal
        title={
          t("liveStreams.recordingInfoModal.title") || "Recording Information"
        }
        open={showRecordingInfoModal}
        onOk={() => setShowRecordingInfoModal(false)}
        onCancel={() => {
          setShowRecordingInfoModal(false);
          form.setFieldsValue({ isRecording: false });
        }}
        centered
        okText={t("liveStreams.recordingInfoModal.okText") || "I Understand"}
        cancelText={
          t("liveStreams.recordingInfoModal.cancelText") || "Cancel Recording"
        }
      >
        <p>
          {t("liveStreams.recordingInfoModal.description") ||
            "When recording is enabled, the live stream will be automatically saved and stored for 30 days."}
        </p>
        <p>
          {t("liveStreams.recordingInfoModal.deletionInfo") ||
            "After 30 days, the recording will be permanently deleted."}
        </p>
      </Modal>

      <Modal
        title={
          isEditMode
            ? t("liveStreams.edit.title") || "Edit Live Stream"
            : t("liveStreams.createModal.modal.title") || "Create Live Stream"
        }
        open={isVisible}
        onCancel={() => {
          dispatch(closeModal());
          form.resetFields();
        }}
        centered
        width={750}
        footer={[
          <Button
            key="back"
            onClick={() => {
              dispatch(closeModal());
              form.resetFields();
            }}
          >
            {t("common.cancel") || "Cancel"}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            icon={isEditMode ? <EditOutlined /> : <PlusOutlined />}
          >
            {isEditMode
              ? t("liveStreams.edit.button") || "Update"
              : t("liveStreams.createModal.button") || "Create"}
          </Button>,
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.title") || "Title"}
                name="title"
                rules={[
                  {
                    required: true,
                    message:
                      t("liveStreams.form.titleRequired") ||
                      "Title is required",
                  },
                ]}
              >
                <Input
                  placeholder={
                    t("liveStreams.form.titlePlaceholder") ||
                    "Enter live stream title"
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.status") || "Status"}
                name="status"
                initialValue="scheduled"
                rules={[
                  {
                    required: true,
                    message:
                      t("liveStreams.form.statusRequired") ||
                      "Status is required",
                  },
                ]}
              >
                <Select
                  options={[
                    { value: "scheduled", label: "Scheduled" },
                    { value: "live", label: "Live" },
                    ...(isEditMode
                      ? [
                          { value: "ended", label: "Ended" },
                          { value: "cancelled", label: "Cancelled" },
                        ]
                      : []),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t("liveStreams.form.description") || "Description"}
            name="description"
          >
            <Input.TextArea
              rows={3}
              placeholder={
                t("liveStreams.form.descriptionPlaceholder") ||
                "Enter description"
              }
            />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.training") || "Training"}
                name="training"
                rules={[
                  {
                    required: true,
                    message:
                      t("liveStreams.form.trainingRequired") ||
                      "Training is required",
                  },
                ]}
              >
                <Select
                  placeholder={
                    t("liveStreams.form.selectTraining") || "Select training"
                  }
                  options={(trainings || [])?.map((tr) => ({
                    value: tr?._id,
                    label: tr?.name,
                  }))}
                  onChange={handleTrainingChange}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.instructor") || "Instructor"}
                name="instructor"
                rules={[
                  {
                    required: true,
                    message:
                      t("liveStreams.form.instructorRequired") ||
                      "Instructor is required",
                  },
                ]}
              >
                <Select
                  disabled={!selectedTraining}
                  placeholder={
                    selectedTraining
                      ? t("liveStreams.form.selectInstructor") ||
                        "Select instructor"
                      : t("liveStreams.form.selectTrainingFirst") ||
                        "Select a training first"
                  }
                  notFoundContent={
                    t("liveStreams.form.noInstructorForTraining") ||
                    "No instructor enrolled in this training"
                  }
                  options={trainingInstructors.map((ins) => ({
                    value: ins?._id,
                    label: ins?.fullName,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.group") || "Group"}
                name="group"
              >
                <Select
                  allowClear
                  placeholder={
                    t("liveStreams.form.selectGroup") ||
                    "Select group (optional)"
                  }
                  options={(filteredGroups || [])?.map((g) => ({
                    value: g?._id,
                    label: g?.groupName,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.isRecording") || "Enable Recording"}
                name="isRecording"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch onChange={handleRecordingChange} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }} />

          {/* Access control */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  t("liveStreams.form.restrictStudents") ||
                  "Restrict to specific students"
                }
                name="restrictToSpecificStudents"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  t("liveStreams.form.allowedStudents") || "Allowed students"
                }
                name="allowedStudentIds"
                rules={
                  restrictToSpecificStudents
                    ? [
                        {
                          required: true,
                          message:
                            t("liveStreams.form.allowedStudentsRequired") ||
                            "Select at least one student",
                        },
                      ]
                    : []
                }
              >
                <Select
                  mode="multiple"
                  disabled={!restrictToSpecificStudents}
                  placeholder={
                    t("liveStreams.form.selectStudents") || "Select students"
                  }
                  options={(students || [])?.map((s) => ({
                    value: s?._id,
                    label: s?.fullName,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("liveStreams.form.startTime") || "Start Time"}
                name="scheduledStartTime"
                rules={[
                  {
                    required: true,
                    message:
                      t("liveStreams.form.startTimeRequired") ||
                      "Start time is required",
                  },
                ]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
